import React, { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { canvasArtworks } from "../data/canvasArtworks";
import { homepageEditorialPicks } from "../data/homepagePicks";
import { EditorialPickCard } from "./EditorialPickCard";
import { CanvasLightbox } from "./CanvasLightbox";

type FeaturedLightbox = (typeof homepageEditorialPicks)[number];

export function FeaturedPicksSection({
  canvasHomepage = false,
}: {
  canvasHomepage?: boolean;
}) {
  const { t } = useTranslation();
  const [lightbox, setLightbox] = useState<FeaturedLightbox | null>(null);

  const closeLightbox = useCallback(() => setLightbox(null), []);

  const navigateFeatured = useCallback(
    (direction: -1 | 1) => {
      if (!lightbox) return;
      const picks = [...homepageEditorialPicks];
      const currentIndex = picks.findIndex(
        (p) => p.medium === lightbox.medium && p.id === lightbox.id,
      );
      if (currentIndex === -1) return;
      const nextIndex = (currentIndex + direction + picks.length) % picks.length;
      setLightbox(picks[nextIndex]);
    },
    [lightbox],
  );

  const openPick = useCallback((pick: FeaturedLightbox) => {
    setLightbox(pick);
  }, []);

  const pickHandlers = useMemo(
    () =>
      Object.fromEntries(
        homepageEditorialPicks.map((pick) => [
          `${pick.medium}-${pick.id}`,
          () => openPick(pick),
        ]),
      ) as Record<string, () => void>,
    [openPick],
  );

  const showNav = homepageEditorialPicks.length > 1;

  const canvasWork =
    lightbox != null
      ? canvasArtworks.find((w) => w.id === lightbox.id) ?? null
      : null;

  const galleryHref = "/gallery?tab=canvas";

  const featuredEyebrow = canvasHomepage
    ? t("home.featured.canvasEyebrow")
    : t("home.featured.eyebrow");
  const featuredTitle = canvasHomepage
    ? t("home.featured.canvasTitle")
    : t("home.featured.title");
  const featuredSubtitle = canvasHomepage
    ? t("home.featured.canvasSubtitle")
    : t("home.featured.subtitle");

  return (
    <section className="border-t border-canvas-200/60">
      <div className="section-container py-12 sm:py-16 md:py-20">
        <div className="mx-auto max-w-2xl text-center">
          <p className="text-sm font-medium uppercase tracking-[0.15em] text-accent">
            {featuredEyebrow}
          </p>
          <h2 className="mt-2 font-display text-2xl font-semibold text-canvas-950 sm:text-3xl md:text-4xl">
            {featuredTitle}
          </h2>
          <p className="mt-3 text-sm text-canvas-600 sm:text-base">
            {featuredSubtitle}
          </p>
        </div>
      </div>

      <div>
        {homepageEditorialPicks.map((pick, index) => (
          <EditorialPickCard
            key={`${pick.medium}-${pick.id}`}
            pick={pick}
            index={index}
            onClick={pickHandlers[`${pick.medium}-${pick.id}`]}
          />
        ))}
      </div>

      <div className="section-container border-t border-canvas-200/70 py-10 sm:py-12">
        <div className="flex flex-wrap justify-center gap-3">
          <Link
            to={canvasHomepage ? "/gallery?tab=canvas" : "/gallery"}
            className="btn-secondary !px-5 !py-2.5 !text-sm"
          >
            {canvasHomepage
              ? t("home.featured.ctaCanvasGallery")
              : t("home.featured.ctaGallery")}
          </Link>
          <Link
            to="/contact#commission"
            className="btn-accent !px-5 !py-2.5 !text-sm"
          >
            {t("home.featured.ctaCommission")}
          </Link>
          {canvasHomepage && (
            <Link
              to="/gallery"
              className="border-0 bg-transparent px-4 py-2.5 text-sm font-medium text-canvas-600 underline-offset-4 transition hover:text-accent hover:underline"
            >
              {t("home.featured.ctaFullGallery")}
            </Link>
          )}
        </div>
      </div>

      {canvasWork && (
        <CanvasLightbox
          work={canvasWork}
          onClose={closeLightbox}
          onNavigate={showNav ? navigateFeatured : undefined}
          showNavigation={showNav}
          exploreGalleryHref={galleryHref}
        />
      )}
    </section>
  );
}

export function StudioServicesSection({
  canvasHomepage = false,
}: {
  canvasHomepage?: boolean;
}) {
  const { t } = useTranslation();

  const services = canvasHomepage
    ? (["spaces", "canvas"] as const)
    : (["canvas", "brand", "spaces", "prints", "collab"] as const);

  return (
    <section
      id={canvasHomepage ? "office-collections" : undefined}
      className="border-t border-canvas-200/60 bg-canvas-100/40 py-12 sm:py-16 md:py-20 scroll-mt-20"
    >
      <div className="section-container">
        <div className="mx-auto mb-10 max-w-2xl text-center sm:mb-12">
          <p className="text-sm font-medium uppercase tracking-[0.15em] text-accent">
            {canvasHomepage
              ? t("home.services.canvasEyebrow")
              : t("home.services.eyebrow")}
          </p>
          <h2 className="mt-2 font-display text-2xl font-semibold text-canvas-950 sm:text-3xl md:text-4xl">
            {canvasHomepage
              ? t("home.services.canvasTitle")
              : t("home.services.title")}
          </h2>
          <p className="mt-3 text-sm text-canvas-600 sm:text-base">
            {canvasHomepage
              ? t("home.services.canvasSubtitle")
              : t("home.services.subtitle")}
          </p>
        </div>

        <div
          className={`grid gap-4 sm:gap-5 ${
            canvasHomepage
              ? "sm:grid-cols-2"
              : "sm:grid-cols-2 lg:grid-cols-3"
          }`}
        >
          {services.map((key) => (
            <div
              key={key}
              className={`rounded-2xl bg-white shadow-sm ring-1 ring-canvas-200/60 sm:p-6 ${
                canvasHomepage && key === "spaces"
                  ? "p-6 sm:p-8 ring-accent/25"
                  : "p-5"
              }`}
            >
              <p className="text-[10px] font-medium uppercase tracking-[0.15em] text-accent">
                {t(`home.services.items.${key}.title`)}
              </p>
              <p className="mt-3 text-sm leading-relaxed text-canvas-600">
                {t(`home.services.items.${key}.description`)}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap justify-center gap-3 sm:mt-10 sm:gap-4">
          <Link to="/contact#commission" className="btn-accent">
            {t("home.services.ctaCommission")}
          </Link>
          {canvasHomepage ? (
            <Link to="/gallery?tab=canvas" className="btn-secondary">
              {t("home.services.ctaCanvas")}
            </Link>
          ) : (
            <Link to="/gallery?tab=ai" className="btn-secondary">
              {t("home.services.ctaBrand")}
            </Link>
          )}
        </div>
      </div>
    </section>
  );
}
